import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError } from "@/components/shared/helpers";

/**
 * Formulaire propriétaire pour créer une demande sur un séjour verrouillé.
 *
 * Types de demandes :
 *   • early_arrival / late_departure : heure demandée + règle des 3h (garde-fou existant)
 *   • modify (dates)     : changement de dates + vérification disponibilité temps réel
 *   • modify (services)  : mise à jour des prestations
 *   • cancel             : annulation + note optionnelle
 *
 * Toutes ces demandes sont GRATUITES pour modify/cancel — aucun prix n'est affiché.
 */
export default function RequestDialog({ stay, property, onDone }) {
  const [open, setOpen] = useState(false);
  const [requestType, setRequestType] = useState("early_arrival");
  // Sous-type de "modify" : "dates" ou "services"
  const [modifySubtype, setModifySubtype] = useState("dates");
  const [timeValue, setTimeValue] = useState("");
  const [note, setNote] = useState("");
  // Changement de dates
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [availability, setAvailability] = useState(null); // { has_hard_conflict, has_soft_conflict, hard, soft } | null
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  // Modification des prestations
  const [modCleaning, setModCleaning] = useState(!!stay?.services?.cleaning);
  const [modKitsS, setModKitsS] = useState(stay?.services?.linen_kits_simple || 0);
  const [modKitsD, setModKitsD] = useState(stay?.services?.linen_kits_double || 0);
  const [modBedsS, setModBedsS] = useState(stay?.services?.beds_made_simple || 0);
  const [modBedsD, setModBedsD] = useState(stay?.services?.beds_made_double || 0);
  const [modHyg, setModHyg] = useState(!!stay?.services?.hygiene_kit);
  const [modDegreasing, setModDegreasing] = useState(!!stay?.services?.degreasing);
  const [modBasket, setModBasket] = useState(stay?.services?.welcome_basket || "none");

  // ─── Règle des 3h pour early_arrival / late_departure ─────────────────
  const slot = (() => {
    const dep = (property?.default_departure_time || "").slice(0, 2);
    const arr = (property?.default_arrival_time || "").slice(0, 2);
    if (dep === "09" || arr === "16") return "09-16";
    return "10-17";
  })();
  const maxLateDeparture = slot === "09-16" ? "12:00" : "13:00";
  const minEarlyArrival = slot === "09-16" ? "13:00" : "14:00";

  useEffect(() => {
    if (!open) return;
    // Réinitialise le formulaire à chaque ouverture
    setNote("");
    setNewStart(stay?.start_date || "");
    setNewEnd(stay?.end_date || "");
    setAvailability(null);
    setModifySubtype("dates");
    setModCleaning(!!stay?.services?.cleaning);
    setModKitsS(stay?.services?.linen_kits_simple || 0);
    setModKitsD(stay?.services?.linen_kits_double || 0);
    setModBedsS(stay?.services?.beds_made_simple || 0);
    setModBedsD(stay?.services?.beds_made_double || 0);
    setModHyg(!!stay?.services?.hygiene_kit);
    setModDegreasing(!!stay?.services?.degreasing);
    setModBasket(stay?.services?.welcome_basket || "none");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (requestType === "late_departure") setTimeValue(maxLateDeparture);
    else if (requestType === "early_arrival") setTimeValue(minEarlyArrival);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestType, open, slot]);

  // ─── Vérification temps réel des disponibilités (debounced 400ms) ─────
  useEffect(() => {
    if (requestType !== "modify" || modifySubtype !== "dates") {
      setAvailability(null);
      return;
    }
    if (!newStart || !newEnd) {
      setAvailability(null);
      return;
    }
    // Ignore si les dates sont identiques au séjour actuel
    if (newStart === stay?.start_date && newEnd === stay?.end_date) {
      setAvailability(null);
      return;
    }
    if (newStart >= newEnd) {
      setAvailability({ has_hard_conflict: false, has_soft_conflict: false, hard: [], soft: [], invalid: true });
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/owner/stays/${stay.id}/date-availability`, {
          params: { start: newStart, end: newEnd },
        });
        if (!cancelled) setAvailability(res.data);
      } catch (e) {
        if (!cancelled) setAvailability({ has_hard_conflict: false, has_soft_conflict: false, hard: [], soft: [], error: formatError(e) });
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [newStart, newEnd, requestType, modifySubtype, stay?.id, stay?.start_date, stay?.end_date]);

  const hardBlocked = availability?.has_hard_conflict === true || availability?.invalid === true;
  const softWarning = availability?.has_soft_conflict === true && !availability?.has_hard_conflict;

  const submit = async () => {
    try {
      let payload = {};
      let finalRequestType = requestType;

      if (requestType === "early_arrival" || requestType === "late_departure") {
        if (requestType === "late_departure" && timeValue > maxLateDeparture) {
          toast({ title: "Heure trop tardive", description: `Pour le créneau ${slot}, l'heure de départ tardif ne peut pas dépasser ${maxLateDeparture}.`, variant: "destructive" });
          return;
        }
        if (requestType === "early_arrival" && timeValue < minEarlyArrival) {
          toast({ title: "Heure trop précoce", description: `Pour le créneau ${slot}, l'heure d'arrivée anticipée ne peut pas être avant ${minEarlyArrival}.`, variant: "destructive" });
          return;
        }
        payload = { requested_time: timeValue };
      }

      if (requestType === "cancel") {
        payload = note.trim() ? { note: note.trim() } : {};
      }

      if (requestType === "modify") {
        finalRequestType = "modify";
        if (modifySubtype === "dates") {
          if (!newStart || !newEnd) {
            toast({ title: "Dates requises", description: "Veuillez renseigner les nouvelles dates d'arrivée et de départ.", variant: "destructive" });
            return;
          }
          if (newStart >= newEnd) {
            toast({ title: "Dates invalides", description: "La date de départ doit être postérieure à la date d'arrivée.", variant: "destructive" });
            return;
          }
          if (hardBlocked) {
            toast({ title: "Dates indisponibles", description: "Ces dates chevauchent un séjour déjà confirmé. Choisissez d'autres dates.", variant: "destructive" });
            return;
          }
          payload = {
            start_date: newStart,
            end_date: newEnd,
            note: note.trim() || null,
          };
        } else {
          // modifySubtype === "services"
          const kitsS = Number(modKitsS || 0);
          const kitsD = Number(modKitsD || 0);
          const bedsS = Number(modBedsS || 0);
          const bedsD = Number(modBedsD || 0);
          if (bedsS > kitsS) {
            toast({ title: "Lits simples invalides", description: `Impossible d'avoir ${bedsS} lits simples avec seulement ${kitsS} kit(s).`, variant: "destructive" });
            return;
          }
          if (bedsD > kitsD) {
            toast({ title: "Lits doubles invalides", description: `Impossible d'avoir ${bedsD} lits doubles avec seulement ${kitsD} kit(s).`, variant: "destructive" });
            return;
          }
          payload = {
            note: note.trim() || null,
            services: {
              cleaning: !!modCleaning,
              linen_kits_simple: kitsS,
              linen_kits_double: kitsD,
              beds_made_simple: bedsS,
              beds_made_double: bedsD,
              hygiene_kit: !!modHyg,
              welcome_basket: modBasket,
              degreasing: !!modDegreasing,
              puericulture_items: stay?.services?.puericulture_items || [],
            },
          };
        }
      }

      await api.post(`/owner/stays/${stay.id}/requests`, {
        request_type: finalRequestType,
        payload,
      });
      toast({ title: "Demande envoyée", description: "Elle sera traitée par l'équipe." });
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
  };

  const canModifyCancel = !!stay?.locked;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`owner-stay-request-open-${stay.id}`}
          variant="outline"
          className="border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"
        >
          Demande
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`owner-stay-request-dialog-${stay.id}`} className="border-white/10 bg-[#0f1320] text-slate-100 sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid={`owner-stay-request-title-${stay.id}`}>Créer une demande</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-xl border border-white/10 bg-white/[0.08] p-3">
            <div data-testid={`owner-stay-request-stayinfo-${stay.id}`} className="text-sm text-slate-200">
              <div><b>Séjour</b> : {formatDateEU(stay.start_date)} → {formatDateEU(stay.end_date)}</div>
              <div className="mt-1 text-xs text-slate-300">Après J-7, l'annulation et la modification passent par une demande.</div>
            </div>
          </div>

          <div className="space-y-1">
            <Label data-testid={`owner-stay-request-type-label-${stay.id}`}>Type de demande</Label>
            <select
              data-testid={`owner-stay-request-type-select-${stay.id}`}
              className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
              value={requestType}
              onChange={(e) => setRequestType(e.target.value)}
            >
              <option data-testid={`owner-stay-request-type-early-${stay.id}`} value="early_arrival">Arrivée anticipée</option>
              <option data-testid={`owner-stay-request-type-late-${stay.id}`} value="late_departure">Départ tardif</option>
              <option data-testid={`owner-stay-request-type-modify-${stay.id}`} value="modify" disabled={!canModifyCancel}>Modification de séjour</option>
              <option data-testid={`owner-stay-request-type-cancel-${stay.id}`} value="cancel" disabled={!canModifyCancel}>Annulation de séjour</option>
            </select>
          </div>

          {/* ───── Type : arrivée anticipée / départ tardif (existant) ───── */}
          {(requestType === "early_arrival" || requestType === "late_departure") && (
            <div className="space-y-1">
              <Label data-testid={`owner-stay-request-time-label-${stay.id}`}>Heure demandée</Label>
              <Input
                data-testid={`owner-stay-request-time-input-${stay.id}`}
                type="time"
                className="border-white/10 bg-white/[0.08]"
                value={timeValue}
                max={requestType === "late_departure" ? maxLateDeparture : undefined}
                min={requestType === "early_arrival" ? minEarlyArrival : undefined}
                onChange={(e) => setTimeValue(e.target.value)}
              />
              <div data-testid={`owner-stay-request-time-hint-${stay.id}`} className="text-xs text-slate-300">
                {requestType === "late_departure" ? (
                  <>Heure <strong>maximale</strong> selon votre créneau logement ({slot}) : <strong>{maxLateDeparture}</strong> (règle des 3h).</>
                ) : (
                  <>Heure <strong>minimale</strong> selon votre créneau logement ({slot}) : <strong>{minEarlyArrival}</strong> (règle des 3h).</>
                )}
                <br />
                <span data-testid={`owner-stay-request-price-hint-${stay.id}`}>
                  Prix : <strong>{Number(property?.pricing?.early_late_price_ttc || 25).toFixed(2).replace(/\.00$/, "")}€ TTC</strong> sous réserve d&apos;accord.
                </span>
              </div>
            </div>
          )}

          {/* ───── Type : Modification de séjour (dates OU services) ───── */}
          {requestType === "modify" && (
            <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.08] p-3">
              <div className="space-y-1">
                <Label data-testid={`owner-stay-request-modify-subtype-label-${stay.id}`}>Que souhaitez-vous modifier ?</Label>
                <select
                  data-testid={`owner-stay-request-modify-subtype-${stay.id}`}
                  className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                  value={modifySubtype}
                  onChange={(e) => setModifySubtype(e.target.value)}
                >
                  <option data-testid={`owner-stay-request-modify-subtype-dates-${stay.id}`} value="dates">📅 Changement de dates</option>
                  <option data-testid={`owner-stay-request-modify-subtype-services-${stay.id}`} value="services">🧺 Modification des prestations</option>
                </select>
              </div>

              {modifySubtype === "dates" && (
                <div className="space-y-2">
                  <div className="text-xs text-slate-300" data-testid={`owner-stay-request-modify-current-${stay.id}`}>
                    Dates actuelles : <strong>{formatDateEU(stay.start_date)}</strong> → <strong>{formatDateEU(stay.end_date)}</strong>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label data-testid={`owner-stay-request-modify-newstart-label-${stay.id}`} className="text-xs text-slate-200">Nouvelle arrivée</Label>
                      <Input
                        data-testid={`owner-stay-request-modify-newstart-${stay.id}`}
                        type="date"
                        className="border-white/10 bg-white/[0.08]"
                        value={newStart}
                        onChange={(e) => setNewStart(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label data-testid={`owner-stay-request-modify-newend-label-${stay.id}`} className="text-xs text-slate-200">Nouveau départ</Label>
                      <Input
                        data-testid={`owner-stay-request-modify-newend-${stay.id}`}
                        type="date"
                        className="border-white/10 bg-white/[0.08]"
                        value={newEnd}
                        onChange={(e) => setNewEnd(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Feedback disponibilité temps réel */}
                  {availabilityLoading && (
                    <div data-testid={`owner-stay-request-avail-loading-${stay.id}`} className="text-xs text-slate-400">Vérification des disponibilités…</div>
                  )}
                  {availability?.invalid && (
                    <div data-testid={`owner-stay-request-avail-invalid-${stay.id}`} className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200">
                      La date de départ doit être postérieure à la date d'arrivée.
                    </div>
                  )}
                  {availability?.has_hard_conflict && (
                    <div data-testid={`owner-stay-request-avail-hard-${stay.id}`} className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200 space-y-1">
                      <div className="font-medium">⛔ Ces dates ne sont pas disponibles</div>
                      {availability.hard.map((c, i) => (
                        <div key={i}>Un séjour est déjà confirmé du <strong>{formatDateEU(c.start_date)}</strong> au <strong>{formatDateEU(c.end_date)}</strong>.</div>
                      ))}
                      <div>Veuillez choisir d'autres dates.</div>
                    </div>
                  )}
                  {softWarning && (
                    <div data-testid={`owner-stay-request-avail-soft-${stay.id}`} className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-100 space-y-1">
                      <div className="font-medium">⚠️ Attention : ces dates semblent occupées</div>
                      {availability.soft.map((c, i) => (
                        <div key={i}>
                          Selon votre calendrier {c.ical_source ? <strong>{c.ical_source}</strong> : "externe"}, la période du {formatDateEU(c.start_date)} au {formatDateEU(c.end_date)} paraît réservée.
                        </div>
                      ))}
                      <div>Vérifiez avant de soumettre.</div>
                    </div>
                  )}
                </div>
              )}

              {modifySubtype === "services" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div data-testid={`owner-stay-request-modify-cleaning-label-${stay.id}`} className="text-sm">🧹 Ménage</div>
                    <Checkbox
                      data-testid={`owner-stay-request-modify-cleaning-${stay.id}`}
                      checked={!!modCleaning}
                      onCheckedChange={(v) => setModCleaning(!!v)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label data-testid={`owner-stay-request-modify-kits-s-label-${stay.id}`} className="text-xs text-slate-200">🛏 Kits linge simple</Label>
                      <Input data-testid={`owner-stay-request-modify-kits-s-${stay.id}`} type="number" min={0} className="border-white/10 bg-white/[0.08]" value={modKitsS} onChange={(e) => setModKitsS(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label data-testid={`owner-stay-request-modify-kits-d-label-${stay.id}`} className="text-xs text-slate-200">🛏 Kits linge double</Label>
                      <Input data-testid={`owner-stay-request-modify-kits-d-${stay.id}`} type="number" min={0} className="border-white/10 bg-white/[0.08]" value={modKitsD} onChange={(e) => setModKitsD(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label data-testid={`owner-stay-request-modify-beds-s-label-${stay.id}`} className="text-xs text-slate-200">🛏 Lits faits simple</Label>
                      <Input data-testid={`owner-stay-request-modify-beds-s-${stay.id}`} type="number" min={0} className="border-white/10 bg-white/[0.08]" value={modBedsS} onChange={(e) => setModBedsS(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label data-testid={`owner-stay-request-modify-beds-d-label-${stay.id}`} className="text-xs text-slate-200">🛏 Lits faits double</Label>
                      <Input data-testid={`owner-stay-request-modify-beds-d-${stay.id}`} type="number" min={0} className="border-white/10 bg-white/[0.08]" value={modBedsD} onChange={(e) => setModBedsD(e.target.value)} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div data-testid={`owner-stay-request-modify-hyg-label-${stay.id}`} className="text-sm">🧴 Kit hygiène</div>
                    <Checkbox data-testid={`owner-stay-request-modify-hyg-${stay.id}`} checked={!!modHyg} onCheckedChange={(v) => setModHyg(!!v)} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div data-testid={`owner-stay-request-modify-degreasing-label-${stay.id}`} className="text-sm">🧽 Dégraissage</div>
                    <Checkbox data-testid={`owner-stay-request-modify-degreasing-${stay.id}`} checked={!!modDegreasing} onCheckedChange={(v) => setModDegreasing(!!v)} />
                  </div>
                  <div className="space-y-1">
                    <Label data-testid={`owner-stay-request-modify-basket-label-${stay.id}`} className="text-xs text-slate-200">🧺 Panier d'accueil</Label>
                    <select
                      data-testid={`owner-stay-request-modify-basket-${stay.id}`}
                      className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                      value={modBasket}
                      onChange={(e) => setModBasket(e.target.value)}
                    >
                      <option value="none">Aucun</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div data-testid={`owner-stay-request-modify-hint-${stay.id}`} className="text-xs text-slate-300">
                    Rappel : lits faits impossible si kits linge = 0.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ───── Type : Annulation ───── */}
          {requestType === "cancel" && (
            <div data-testid={`owner-stay-request-cancel-warning-${stay.id}`} className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-100 space-y-1">
              <div className="font-medium">🚫 Annulation de séjour</div>
              <div>Confirmez-vous l'annulation du séjour du {formatDateEU(stay.start_date)} au {formatDateEU(stay.end_date)} ? L'équipe validera votre demande.</div>
            </div>
          )}

          {/* Note optionnelle (tous types sauf early/late qui ont déjà l'heure) */}
          {requestType !== "early_arrival" && requestType !== "late_departure" && (
            <div className="space-y-1">
              <Label data-testid={`owner-stay-request-note-label-${stay.id}`}>Note (optionnelle)</Label>
              <Textarea
                data-testid={`owner-stay-request-note-${stay.id}`}
                className="border-white/10 bg-white/[0.08]"
                placeholder="Ajoutez une précision si nécessaire."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              data-testid={`owner-stay-request-cancelbtn-${stay.id}`}
              variant="outline"
              className="border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"
              onClick={() => setOpen(false)}
            >
              Fermer
            </Button>
            <Button
              data-testid={`owner-stay-request-submit-${stay.id}`}
              disabled={requestType === "modify" && modifySubtype === "dates" && hardBlocked}
              className={`rounded-full text-white ${(requestType === "modify" && modifySubtype === "dates" && hardBlocked) ? "bg-violet-500/30 cursor-not-allowed" : "bg-violet-500 hover:bg-violet-400"}`}
              onClick={submit}
            >
              Envoyer la demande
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
